import Term from "../../term/Term";
import styles from "../css/Flashcards.module.css";
import SpeakButton from "../../common/SpeakButton";
import { useEffect, useState } from "react";
import {
  getQuestionTypeFromLocalStorage,
  isOnLearningTextToSpeechFromLocalStorage,
} from "../../settings/util/learningSettings";
import { LearningType } from "../model/LearningType";
import { QuestionType } from "../../settings/model/QuestionType";
import BigButton from "../../common/BigButton";
import { useTranslation } from "react-i18next";
import { isShowMemo, isShowPronunciation } from "../util/learning";
import AudioPlayer from "react-h5-audio-player";
import {
  EnglishPronunciation,
  getVoiceFile,
  makeBase64String,
} from "../../voice/util/voice";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import { auth } from "../../../configurations/firebaseConfig";
import { motion } from "framer-motion";
import { isHiraganaOnly } from "../../common/strings";
import { useReadingSettings } from "../../common/util/chineseCharacterMode";
import PronunciationSelectView from "../../common/view/PronunciationSelectView";
import MarkButton from "../../common/MarkButton";
import { formatShortcut } from "../../common/util/shourtcut";

interface FlashcardsProps {
  term: Term;
  englishPronunciation: EnglishPronunciation;
  showingPronunciationSelectView: boolean;
  onUpdateMark: (term: Term) => void;
  onCorrect: (term: Term) => void;
  onIncorrect: (term: Term) => void;
  onSpeakError: (error: string) => void;
  onClickPronunciationSelectButton: (
    pronunciation: EnglishPronunciation
  ) => void;
}

export default function Flashcards(props: FlashcardsProps) {
  const { t } = useTranslation();
  const { chineseCharacterMode } = useReadingSettings();

  const [audioSrc, setAudioSrc] = useState<string>();
  const [front, setFront] = useState<"front" | "back">("front");
  const randomChoice = Math.random() < 0.5 ? "term" : "definition";
  const [currentQuestion, setCurrentQuestion] = useState<
    "term" | "definition" | "pron"
  >(getCurrentQuestionType());

  const [question, setQuestion] = useState("");
  const [questionUpdateCount, setQuestionUpdateCount] = useState(0);
  const [currentGroup, setCurrentGroup] = useState("");
  const [currentGroupUpdateCount, setCurrentGroupUpdateCount] = useState(0);
  const [speakCount, setSpeakCount] = useState(0);

  const [isPlaying, setIsPlaying] = useState(false);

  const speechShourtCut = formatShortcut({ mac: "⌘ S", win: "Ctrl + S" });

  // 같은 카드에서 단어를 편집하거나 별표를 눌러도 진행 상태가 초기화되지 않도록 id 가 바뀔 때만 새로 시작한다
  useEffect(() => {
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.term.id]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isSave = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s";

      if (!isSave) return;

      e.preventDefault();
      speak();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentGroupUpdateCount(currentGroupUpdateCount + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup]);

  useEffect(() => {
    setQuestionUpdateCount(questionUpdateCount + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (
        speakCount === 0 &&
        currentGroupUpdateCount > 0 &&
        questionUpdateCount > 0 &&
        auth.currentUser !== null
      ) {
        if (!isOnLearningTextToSpeechFromLocalStorage()) return;
        if (currentQuestion === "definition") return;
        speak();
      }
    }, 50);

    return () => {
      clearTimeout(handler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, currentGroup]);

  useEffect(() => {
    let newQuestionString = "";
    switch (front) {
      case "front":
        switch (currentQuestion) {
          case "term":
            newQuestionString = props.term.term;
            break;
          case "definition":
            newQuestionString = props.term.definition;
            break;
          case "pron":
            newQuestionString = t("GuessTheDefinitionWithPronunciation");
            break;
        }
        break;
      case "back":
        switch (currentQuestion) {
          case "term":
            newQuestionString = props.term.definition;
            break;
          case "definition":
            newQuestionString = props.term.term;
            break;
          case "pron":
            newQuestionString = props.term.term + "\n" + props.term.definition;
            break;
        }
        break;
    }

    setQuestion(newQuestionString);
    setCurrentGroup(props.term.group);
  }, [currentQuestion, front, props.term, t]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        handleOnClickContainer();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function reload() {
    setSpeakCount(0);
    updateCurrentQuestionType();
  }

  function getCurrentQuestionType(): "term" | "definition" | "pron" {
    switch (getQuestionTypeFromLocalStorage(LearningType.flashcards)) {
      case QuestionType.termFirst:
      case QuestionType.showAll:
        return "term";
      case QuestionType.definitionFirst:
        return "definition";
      case QuestionType.pronFirst:
        return "pron";
      case QuestionType.random:
        return randomChoice;
    }
  }

  function updateCurrentQuestionType() {
    switch (getQuestionTypeFromLocalStorage(LearningType.flashcards)) {
      case QuestionType.termFirst:
        setCurrentQuestion("term");
        break;
      case QuestionType.definitionFirst:
        setCurrentQuestion("definition");
        break;
      case QuestionType.random:
        setCurrentQuestion(randomChoice);
        break;
    }
  }

  function handleOnClickContainer() {
    setFront((prevFront) => (prevFront === "front" ? "back" : "front"));
  }

  function handleOnClickMarkButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.onUpdateMark(props.term);
  }

  function handleOnClickSpeakButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    speak();
  }

  function handleOnClickKnowButton(e?: React.MouseEvent<HTMLDivElement>) {
    e?.stopPropagation();
    setFront("front");
    props.onCorrect(props.term);
  }

  function handleOnClickStillLearningButton(
    e?: React.MouseEvent<HTMLDivElement>
  ) {
    e?.stopPropagation();
    setFront("front");
    props.onIncorrect(props.term);
  }

  async function speak() {
    setSpeakCount(speakCount + 1);
    setIsPlaying(true);

    let text = "";
    let languageCode = "";

    switch (currentQuestion) {
      case "term":
      case "pron":
        switch (front) {
          case "front":
            text = isHiraganaOnly(props.term.pronunciation)
              ? props.term.pronunciation
              : props.term.term;
            languageCode =
              currentGroup.length !== 0
                ? await fetchGroupLanguageCode(currentGroup)
                : "";
            break;
          case "back":
            text = props.term.definition;
            languageCode = "";
            break;
        }
        break;
      case "definition":
        switch (front) {
          case "front":
            text = props.term.definition;
            languageCode = "";
            break;
          case "back":
            text = isHiraganaOnly(props.term.pronunciation)
              ? props.term.pronunciation
              : props.term.term;
            languageCode =
              currentGroup.length !== 0
                ? await fetchGroupLanguageCode(currentGroup)
                : "";
            break;
        }
        break;
    }

    try {
      const audio = await getVoiceFile(text, languageCode);
      setAudioSrc(makeBase64String(audio));
    } catch (error) {
      if (error instanceof Error) {
        props.onSpeakError(error.message);
      }
    }
  }

  return (
    <>
      <motion.div
        className={styles.containerForMotion}
        key={props.term.id}
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{
          duration: 0.3,
        }}
      >
        <div
          className={styles.container}
          onClick={() => handleOnClickContainer()}
        >
          <div className={styles.topContainer}>
            <div className={styles.topLeftContainer}>
              <MarkButton
                isMarked={props.term.isMarked}
                onClick={handleOnClickMarkButton}
              />
              <SpeakButton
                fontSize="small"
                isPlaying={isPlaying}
                onClick={handleOnClickSpeakButton}
                title={speechShourtCut}
              />
            </div>
            <div className={styles.topRightContainer}>
              <PronunciationSelectView
                open={props.showingPronunciationSelectView}
                selectedPronunciation={props.englishPronunciation}
                onClickAction={(
                  e: React.MouseEvent<HTMLDivElement>,
                  pronunciation: EnglishPronunciation
                ) => {
                  handleOnClickSpeakButton(e);
                  props.onClickPronunciationSelectButton(pronunciation);
                }}
              />
            </div>
          </div>
          <div className={styles.contentsContainer}>
            {front === "front" && (
              <div className={styles.questionContainer}>
                {isShowPronunciation(
                  props.term.pronunciation,
                  front,
                  currentQuestion
                ) === true && (
                  <p
                    className={styles.pronunciationText}
                    lang={
                      chineseCharacterMode === "japanese"
                        ? "ja"
                        : navigator?.language
                    }
                  >
                    {`[${props.term.pronunciation}]`}
                  </p>
                )}
                <p
                  className={styles.questionText}
                  lang={
                    chineseCharacterMode === "japanese"
                      ? "ja"
                      : navigator?.language
                  }
                >
                  {question}
                </p>
                {isShowMemo(props.term.memo, front, LearningType.flashcards) && (
                  <p
                    className={styles.memoText}
                    lang={
                      chineseCharacterMode === "japanese"
                        ? "ja"
                        : navigator?.language
                    }
                  >
                    {props.term.memo}
                  </p>
                )}
              </div>
            )}
            {front === "back" && (
              <div className={styles.answerContainer}>
                {isShowPronunciation(
                  props.term.pronunciation,
                  front,
                  currentQuestion
                ) === true && (
                  <p
                    className={styles.pronunciationText}
                    lang={
                      chineseCharacterMode === "japanese"
                        ? "ja"
                        : navigator?.language
                    }
                  >
                    {`[${props.term.pronunciation}]`}
                  </p>
                )}
                <p className={styles.questionText}>{question}</p>
                {isShowMemo(props.term.memo, front, LearningType.flashcards) && (
                  <p
                    className={styles.memoText}
                    lang={
                      chineseCharacterMode === "japanese"
                        ? "ja"
                        : navigator?.language
                    }
                  >
                    {props.term.memo}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className={styles.buttonsContainer}>
            <BigButton
              title={t("StillLearningButton")}
              variant="clear"
              buttonNumber={1}
              hoverColor="var(--level-one-color)"
              handleClick={handleOnClickStillLearningButton}
            />
            <BigButton
              title={t("KnowButton")}
              variant="clear"
              buttonNumber={2}
              hoverColor="var(--level-three-color)"
              handleClick={handleOnClickKnowButton}
            />
          </div>
          {audioSrc && (
            <AudioPlayer
              autoPlay={true}
              src={audioSrc}
              style={{ display: "none" }}
              onEnded={() => {
                setIsPlaying(false);
                setAudioSrc(undefined);
              }}
            />
          )}
        </div>
      </motion.div>
    </>
  );
}
